/**
 * otelSetup.js
 *
 * Initialises the OpenTelemetry browser SDK for the Frictionless UI.
 *
 * Replaces the legacy SA-logging Axios interceptors (saLogging.js).
 *
 * Design notes
 * ────────────
 * • Gated by `env.serviceLoggingEnabled` — when false, nothing is registered
 *   and zero spans are created (safe for local dev overrides).
 * • A custom MaskingSpanProcessor sits between the SDK and the OTLP
 *   BatchSpanProcessor so that every PII field defined in sensitiveFields.js
 *   is replaced with "****" before any span payload leaves the browser.
 * • OTLP endpoint and headers are sourced exclusively from the env config —
 *   no hardcoded credentials anywhere in this file.
 * • Initialisation is non-blocking: the useEffect caller in App.js fires
 *   asynchronously after first render.
 */
import { WebTracerProvider } from '@opentelemetry/sdk-trace-web';
import { BatchSpanProcessor } from '@opentelemetry/sdk-trace-base';
import { OTLPTraceExporter } from '@opentelemetry/exporter-trace-otlp-http';
import { XMLHttpRequestInstrumentation } from '@opentelemetry/instrumentation-xml-http-request';
import { FetchInstrumentation } from '@opentelemetry/instrumentation-fetch';
import { registerInstrumentations } from '@opentelemetry/instrumentation';
import { Resource } from '@opentelemetry/resources';
import { getConfig } from './env';
import { SENSITIVE_FIELDS } from './global-constants/sensitiveFields';
import version from '../version';

// ─── PII helpers ─────────────────────────────────────────────────────────────

/**
 * Flatten every field name listed in SENSITIVE_FIELDS into a single Set of
 * lower-cased strings for fast O(1) look-ups during span processing.
 */
const buildSensitiveFieldList = () => {
  const fields = new Set();
  const traverse = (node) => {
    if (Array.isArray(node)) {
      node.forEach((f) => fields.add(String(f)));
    } else if (node !== null && typeof node === 'object') {
      Object.values(node).forEach(traverse);
    }
  };
  traverse(SENSITIVE_FIELDS);
  return Array.from(fields);
};

const SENSITIVE_FIELD_LIST = buildSensitiveFieldList();

// ─── MaskingSpanProcessor ─────────────────────────────────────────────────────

/**
 * A SpanProcessor decorator that scrubs PII from span attributes **before**
 * forwarding the span to the underlying processor for export.
 *
 * Any span attribute whose key (case-insensitive) matches or contains a
 * sensitive field name from sensitiveFields.js is replaced with "****".
 *
 * Acceptance Criteria coverage:
 *   Scenario 3 — PII fields masked before span export
 *   Scenario 4 — when disabled, processor is never registered
 */
export class MaskingSpanProcessor {
  constructor(delegate, sessionId) {
    this._delegate = delegate;
    this._sessionId = sessionId || '';
  }

  onStart(span, parentContext) {
    // Stamp session.id as a span attribute so the observability backend
    // groups it under a dedicated "session" section (not flattened into labels).
    span.setAttribute('session.id', this._sessionId);
    this._delegate.onStart(span, parentContext);
  }

  onEnd(span) {
    const attrs = span.attributes;
    if (attrs && typeof attrs === 'object') {
      Object.keys(attrs).forEach((key) => {
        const keySegments = key.split('.');
        const terminalKey = keySegments[keySegments.length - 1];
        // Check if the terminal key matches any sensitive field (case-insensitive)
        const isSensitive = SENSITIVE_FIELD_LIST.some(
          field => field.toLowerCase() === terminalKey.toLowerCase()
        );
        if (isSensitive) {
          // Mutate in-place before the delegate (and ultimately the exporter) sees it
          attrs[key] = '****';
        }
      });
    }
    this._delegate.onEnd(span);
  }

  shutdown() {
    return this._delegate.shutdown();
  }

  forceFlush() {
    return this._delegate.forceFlush();
  }
}

// ─── Provider singleton ───────────────────────────────────────────────────────

let _provider = null;

/** @returns {WebTracerProvider|null} The active provider, or null when OTel is disabled. */
export const getTracerProvider = () => _provider;

// ─── initOtel ────────────────────────────────────────────────────────────────

/**
 * Initialise the WebTracerProvider with:
 *   • OTLP HTTP exporter (endpoint from env.otlp.endpoint)
 *   • MaskingSpanProcessor → BatchSpanProcessor pipeline
 *   • XMLHttpRequestInstrumentation  — auto-spans for Axios/XHR calls
 *   • FetchInstrumentation           — auto-spans for fetch() calls
 *
 * Must be called once, inside a React useEffect, to avoid blocking render.
 *
 * @param {string|undefined} trackingID  Query-string trackingID, stamped as
 *                                       a resource attribute for correlation.
 */
export const initOtel = (trackingID) => {
  const env = getConfig();

  // feature flag gate
  if (!env.serviceLoggingEnabled) {
    return;
  }

  // Safety guard against double-registration (e.g. React Strict Mode double-invoke)
  if (_provider) {
    return;
  }

  const exporter = new OTLPTraceExporter({
    url: env.otlp.endpoint,
    headers: env.otlp.headers || {},
  });

  const batchProcessor = new BatchSpanProcessor(exporter);
  const maskingProcessor = new MaskingSpanProcessor(batchProcessor, trackingID);

  const resource = new Resource({
    'service.name': 'frictionless-ui',
    'service.namespace': 'digital-ecom',
    'service.version': String(version),
    'deployment.environment': env.name,
  });

  _provider = new WebTracerProvider({ resource });
  _provider.addSpanProcessor(maskingProcessor);
  _provider.register();

  // Build a regex that matches either the eSign or logging base-URL so that
  // the W3C traceparent header is injected on all Axios (XHR) and fetch calls.
  const buildCorsPattern = (baseUrl) => {
    const escaped = (baseUrl || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    return escaped ? new RegExp(escaped) : null;
  };

  const corsUrls = [
    buildCorsPattern(env.eSign && env.eSign.baseUrl),
    buildCorsPattern(env.logging && env.logging.baseUrl),
  ].filter(Boolean);

  registerInstrumentations({
    instrumentations: [
      new XMLHttpRequestInstrumentation({
        propagateTraceHeaderCorsUrls: corsUrls,
        ignoreUrls: [new RegExp(env.otlp.endpoint.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))],
      }),
      new FetchInstrumentation({
        propagateTraceHeaderCorsUrls: corsUrls,
        ignoreUrls: [new RegExp(env.otlp.endpoint.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))],
      }),
    ],
    tracerProvider: _provider,
  });
};

// ─── ErrorBoundary span helper ────────────────────────────────────────────────

/**
 * Record a caught React ErrorBoundary JS error as an OTel span event.
 *
 * Replaces `api.logErrorBoundaryMessage()` / `logMessage()` for runtime errors.
 *
 * Acceptance Criteria coverage:
 *   Scenario 5 — ErrorBoundary JS errors captured as OTel span events
 *
 * @param {Error}  error   The caught Error object.
 * @param {{ componentStack: string }} info  React error info from ErrorBoundary.
 */
export const recordErrorBoundarySpan = (error, info) => {
  const env = getConfig();
  if (!env.serviceLoggingEnabled || !_provider) {
    return;
  }

  const tracer = _provider.getTracer('frictionless-ui-error-boundary');
  const span = tracer.startSpan('ErrorBoundary.catch');

  // SpanStatusCode.ERROR = 2  (avoid importing @opentelemetry/api just for the enum)
  span.setStatus({ code: 2, message: error && error.message ? error.message : 'Unknown error' });

  span.recordException({
    name: (error && error.name) || 'Error',
    message: (error && error.message) || '',
    // stack is NOT included to prevent inadvertent PII in file paths / variable names
  });

  // Attach sanitised component stack (no variable values, only component names)
  if (info && info.componentStack) {
    span.setAttribute('react.component_stack', info.componentStack);
  }

  span.end();
};

// ─── Test utility ─────────────────────────────────────────────────────────────

/**
 * Reset the provider singleton — used in unit tests only.
 * @internal
 */
export const _resetProviderForTesting = () => {
  _provider = null;
};
